/**
 * Onboarding step persistence (APP-002 §5, §30).
 *
 * The onboarding route keeps the current step in component state, but the
 * account gate is a designed trap: incomplete onboarding on any other route
 * redirects back to /onboarding, which REMOUNTS the route (and a refresh or a
 * round-trip through an external link does the same). Without persistence
 * every remount reset the flow to step 1.
 *
 * The current step id is therefore mirrored into sessionStorage — like the
 * completion marker it stores ONLY a UI-state string (a step id, no account
 * data, IDs, tokens, plan, or usage) — so any remount resumes where the user
 * left off. sessionStorage (not localStorage) keeps it per-tab and clears
 * when the tab closes, so a fresh visit starts at the welcome step.
 */
import type { OnboardingStepId } from './onboardingSteps';

const STORAGE_KEY = 'ei_onboarding_step_v1';

const KNOWN_STEPS: readonly OnboardingStepId[] = ['welcome', 'account_setup', 'orientation', 'next_action'];

/**
 * Read the persisted onboarding step. Returns null for a missing, malformed,
 * or unknown step id — the caller falls back to 'welcome'. Never throws
 * (storage access can be denied in private mode; persistence is best-effort).
 */
export function readOnboardingStep(): OnboardingStepId | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return KNOWN_STEPS.includes(raw as OnboardingStepId) ? (raw as OnboardingStepId) : null;
  } catch {
    return null;
  }
}

/**
 * Persist the current onboarding step. Failures are swallowed — persistence
 * is a UI convenience; the flow still works (just without resume).
 */
export function writeOnboardingStep(step: OnboardingStepId): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, step);
  } catch {
    /* Storage unavailable — remounts resume at the welcome step. */
  }
}

/**
 * Clear the persisted step. Called when onboarding completes or is skipped:
 * a later replay (or the next account) starts fresh at step 1.
 */
export function clearOnboardingStep(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Storage unavailable — nothing to clear. */
  }
}
